import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import useListReducer from '../shared/useListReducer';
import OverallTraineeReportPage from './OverallTraineeReportPage';
import TraineeList from './TraineeList';
import TraineeListFilter from './TraineeListFilter';
import type { ListRow } from './useColumns';
import useAppAbilities from '@/components/abilities';
import NotFound from '@/components/blocks/NotFound';
import PageHeader from '@/components/common/PageHeader';

export enum ViewType {
    Listing = 'listing',
    ShowReport = 'showReport',
    DownloadReport = 'downloadReport',
}

const ReportsPage = () => {
    const { t } = useTranslation('reports');
    const abilities = useAppAbilities();
    const [state, dispatch] = useListReducer();
    const [viewType, setViewType] = useState(ViewType.Listing);
    const [assessmentRunIdSelected, setAssessmentRunIdSelected] = useState<string | undefined>(undefined);
    const [userIdSelected, setUserIdSelected] = useState<string | undefined>(undefined);
    const [programIdSelected, setProgramIdSelected] = useState<string | undefined>(undefined);
    const [editMode, setEditMode] = useState(false);
    const [programNameSelected, setProgramNameSelected] = useState<string | undefined>(undefined);
    const [startDateSelected, setStartDateSelected] = useState<Date | undefined>(undefined);
    const [endDateSelected, setEndDateSelected] = useState<Date | undefined>(undefined);

    const handleView = useCallback((record: ListRow): void => {
        setViewType(ViewType.ShowReport);
        setAssessmentRunIdSelected(record.assessmentRunId);
        setUserIdSelected(record.userId);
        setProgramIdSelected(record.programId);
        setProgramNameSelected(record.programmeName);
        setStartDateSelected(record.startDate);
        setEndDateSelected(record.endDate);
    }, []);

    const handleDownload = useCallback((record: ListRow): void => {
        setViewType(ViewType.DownloadReport);
        setAssessmentRunIdSelected(record.assessmentRunId);
        setUserIdSelected(record.userId);
        setProgramIdSelected(record.programId);
        setProgramNameSelected(record.programmeName);
    }, []);

    const handleEdit = useCallback((record: ListRow): void => {
        setViewType(ViewType.ShowReport);
        setAssessmentRunIdSelected(record.assessmentRunId);
        setUserIdSelected(record.userId);
        setProgramIdSelected(record.programId);
        setProgramNameSelected(record.programmeName);
        setEditMode(true);
    }, []);

    if (!abilities.canViewReports()) {
        return <NotFound />;
    }

    return (
        <>
            {(viewType === ViewType.Listing || viewType === ViewType.DownloadReport) && (
                <div className="flex w-full flex-col gap-5 lg:gap-10">
                    <PageHeader title={t('title')} noControlsRowGap />
                    <TraineeListFilter state={state} dispatch={dispatch} />
                    <TraineeList
                        state={state}
                        dispatch={dispatch}
                        handleView={handleView}
                        handleDownload={handleDownload}
                        handleEdit={handleEdit}
                    />
                </div>
            )}
            {(viewType === ViewType.DownloadReport || viewType === ViewType.ShowReport) && assessmentRunIdSelected && (
                <OverallTraineeReportPage
                    handleBack={() => {
                        setViewType(ViewType.Listing);
                        setAssessmentRunIdSelected(undefined);
                        setUserIdSelected(undefined);
                        setProgramIdSelected(undefined);
                        setProgramNameSelected(undefined);
                        setEditMode(false);
                        setStartDateSelected(undefined);
                        setEndDateSelected(undefined);
                    }}
                    assessmentRunId={assessmentRunIdSelected}
                    userId={userIdSelected || ''}
                    programId={programIdSelected}
                    programName={programNameSelected}
                    startDate={startDateSelected}
                    endDate={endDateSelected}
                    isDetail={viewType === ViewType.ShowReport}
                    editMode={editMode}
                />
            )}
        </>
    );
};

export default ReportsPage;
